import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'

export const ICON_STOP = 3
export const ICON_START = 1
export const ICON_END = 2

type IProps = {
    title: string
    subtitle?: string
    markerColor?: string
    iconType?: number
    address?: string
    time?: {
        icon?: string
        title: string
        value: string
    }
    distance?: {
        icon?: string
        title: string
        value: string
    }
    speed?: {
        icon?: string
        title: string
        value: string
    }
    stops?: {
        icon?: string
        title: string
        value: string
    }
}

const IconTrip = ({ markerColor }: any) => {
    return (
        <>
            <Icon size="28" color="gray-0">
                <i className="far fa-map-marker"></i>
            </Icon>

            <Icon size="25" color={markerColor || 'blue-500'}>
                <i className="fas fa-map-marker"></i>
            </Icon>
            <Icon style={{ top: -10 }} size="13" color="gray-0">
                <i className="fas fa-location"></i>
            </Icon>
        </>
    )
}

const IconStop = ({ markerColor }: any) => {
    return (
        <>
            <Icon size="20" color={markerColor || 'blue-500'} opacity={0.4}>
                <i className="fas fa-circle"></i>
            </Icon>

            <Icon size="13" color={'gray-0'}>
                <i className="fas fa-circle"></i>
            </Icon>

            <Icon size="8" color={markerColor || 'blue-500'}>
                <i className="fas fa-circle"></i>
            </Icon>
        </>
    )
}

const IconCustom = ({ iconType, markerColor }: any) => {
    if (ICON_STOP === iconType) {
        return <IconStop markerColor={markerColor} />
    }

    return <IconTrip markerColor={markerColor} />
}

const InfoWindowMarker = ({
    title,
    subtitle,
    time,
    speed,
    stops,
    distance,
    markerColor,
    iconType,
    address
}: IProps) => {
    return (
        <StyledContainer isStop={iconType === ICON_STOP}>
            <StyledFirstBlock isStop={iconType === ICON_STOP}>
                <div style={{ paddingRight: 20 }}>
                    <ContainerIcons>
                        <IconCustom iconType={iconType} markerColor={markerColor} />
                    </ContainerIcons>
                </div>

                <div>
                    <div className="info-title" style={{ marginBottom: 2 }}>
                        {i18n(title)}
                        {subtitle && <span className="info-title2">{subtitle}</span>}
                    </div>
                    <div style={{ marginLeft: 0 }} className="info-address info-subtitle">
                        {address}
                    </div>
                </div>
            </StyledFirstBlock>
            <StyledSecondBlock>
                <StyledUlFirst>
                    {time && (
                        <LiStyled>
                            <StyledUl>
                                <li>
                                    <i className={time.icon} />
                                </li>
                                <li className="info-title">{i18n(time.title)}:</li>
                                <li className="info-subtitle">{time.value}</li>
                            </StyledUl>
                        </LiStyled>
                    )}

                    {distance && (
                        <LiStyled>
                            <StyledUl>
                                <li>
                                    <i className={distance.icon} />
                                </li>
                                <li className="info-title">{i18n(distance.title)}:</li>
                                <li className="info-subtitle">{distance.value ? '0': distance.value} Km</li>
                            </StyledUl>
                        </LiStyled>
                    )}

                    {speed && (
                        <LiStyled>
                            <StyledUl>
                                <li>
                                    <i className={speed.icon} />
                                </li>
                                <li className="info-title">{i18n(speed.title)}:</li>
                                <li className="info-subtitle">{speed.value} Km/h</li>
                            </StyledUl>
                        </LiStyled>
                    )}

                    {stops && (
                        <LiStyled>
                            <StyledUl>
                                <li>
                                    <i className={stops.icon} />
                                </li>
                                <li className="info-title">{i18n(stops.title)}:</li>
                                <li className="info-subtitle">{stops.value}</li>
                            </StyledUl>
                        </LiStyled>
                    )}
                </StyledUlFirst>
            </StyledSecondBlock>
        </StyledContainer>
    )
}

export default React.memo(InfoWindowMarker)

const StyledContainer = styled.div`
    height: 100%;
    display: ${({ isStop }) => (isStop ? 'flex' : 'block')};
    padding-top: 5px;

    .info-title {
        font-weight: 600;
        font-size: 13px;
        color: ${({ theme }) => theme['gray-800']};
    }

    .info-title2 {
        font-weight: 600;
        font-size: 13px;
        color: ${({ theme }) => theme['gray-700']};
        &::before {
            content: '|';
            margin: 0px 8px;
            color: ${({ theme }) => theme['gray-300']};
        }
    }

    .info-subtitle {
        font-weight: 400;
        font-size: 12px;
        margin-left: 5px;
        color: ${({ theme }) => theme['gray-700']};
    }
`

const StyledFirstBlock = styled.div`
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: ${({ theme, isStop }) => {
        if (isStop) return 'none'
        else return `1px solid ` + theme['gray-400']
    }};
    padding-bottom: ${({ isStop }) => (isStop ? '0' : '12px')};
    margin-bottom: ${({ isStop }) => (isStop ? '0' : '12px')};

    .info-address {
        font-weight: 400;
        font-size: 12px;
        color: ${({ theme }) => theme['gray-700']};
    }
`

const StyledSecondBlock = styled.div`
    padding-left: 9px;
    padding-right: 16px;

    i {
        font-size: 13px;
        width: 21px;
        color: ${({ theme }) => theme['gray-600']};
    }
`

const ContainerIcons = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Icon = styled.div`
    position: absolute;
    opacity: ${({ opacity }) => opacity || 1};
    font-size: ${({ size }) => `${size}px`};
    color: ${({ theme, color }) => theme[color] || color};
`

const StyledUl = styled.ul`
    display: flex;
`
const StyledUlFirst = styled.ul``

const LiStyled = styled.li`
    margin-bottom: 12px;
`
